// Leaving an online game from the menu resigns it, and the board is gone
// before the server answers. The loss still has to reach the history — from
// the server's snapshot, like every other online result — so the room is held
// open for a moment after the scene has unmounted.
import type { ConnectionManager } from '@bn-games/room-client';
import { ActiveGameStore } from '../history/ActiveGameStore.js';
import { GameRecorder } from '../history/GameRecorder.js';
import { commitRecord } from '../history/serverSync.js';
import type { ClientMessage, ServerMessage } from '../network/protocol.js';

/** How long to wait for the server to confirm a resignation sent on the way out. */
const RESIGN_ECHO_TIMEOUT_MS = 2_000;

/**
 * Wait for the snapshot that ends the game, record it, then call `done`
 * (also on timeout: an unreachable server must not hold the room forever).
 * Returns a function that settles at once, for when the room is needed again.
 */
export function recordResignationOnLeave(
  connection: ConnectionManager<ClientMessage, ServerMessage>,
  done: () => void,
): () => void {
  // The saved game names our seat; the scene that knew it is already gone.
  const seat = ActiveGameStore.peek();
  let settled = false;
  const settle = (): void => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    off();
    done();
  };
  const off = connection.onMessage((msg) => {
    if (msg.type !== 'STATE_SYNC' || msg.snapshot.outcome === null) return;
    const record = seat === null ? null : new GameRecorder().observe(msg.snapshot, seat);
    if (record !== null) commitRecord(record);
    settle();
  });
  const timer = setTimeout(settle, RESIGN_ECHO_TIMEOUT_MS);
  return settle;
}
